import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import TaskCreationLKValues from "../../../../interfaces/lkValues/TaskCreationLKValues";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getTaskLKValues } from "../../../../services/taskServices/TaskLKService";
import {
    publishTask,
    publishTemplateInterimTask,
} from "../../../../services/taskServices/TaskService";
import NewTask from "../../../../interfaces/task/NewTask";
import { useNavigate } from "react-router-dom";
import { getAllParticipants, GetAllParticipantUnderMeAsync } from "../../../../services/participantServices/ParticipantService";
import Participant from "../../../../interfaces/Participant";
import InterimTemplateResponse from "../../../../interfaces/task/InterimTemplateResponse";
import { getDateXDaysInFuture } from "../../../../utilities/dateUtils";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

interface OptionType {
    value: string;
    label: string;
}

interface TaskCreationFromTemplateProps {
    template: InterimTemplateResponse | undefined;
}

const TaskCreationFromTemplate: React.FC<TaskCreationFromTemplateProps> = ({
    template,
}) => {
    const { currentUser, currentOrganization, currentRoles } = useAuth();
    const navigate = useNavigate();
    const [isSC, setIsSC] = useState<boolean>(false);

    const [participantOptions, setParticipantOptions] = useState<OptionType[]>(
        []
    );
    const [ready, setReady] = useState<boolean>(false);

    const [assigneeOptions, setAssigneeOptions] = useState<OptionType[]>([]);

    const [loading, setLoading] = useState<{ participant: boolean; assignee: boolean }>({
        participant: false,
        assignee: false,
    });

    const [newTask, setNewTask] = useState<NewTask>({
        title: template ? template.title : "",
        description: template ? template.description : "",
        requiredActions: "",
        requiredFiles: template
            ? template.requiredFiles.map((file) => file.id.toString())
            : [],
        assignee: undefined,
        coAssignee: undefined,
        participant: undefined,
        templateTaskId: template ? template.id.toString() : "",
        taskOrganization: "1",
        taskType: "1",
        taskSubType: "4",
        dueDate: template?.daysFromCreationDueDate
            ? getDateXDaysInFuture(template.daysFromCreationDueDate)
            : "",
        workflowGroup: "Standard",
        priority: "Low",
    });

    const [lkOptions, setLkOptions] = useState<
        TaskCreationLKValues | null | undefined
    >();

    // useEffect for changing the assignee
    useEffect(() => {
        getAllParticipantUnderMe();
    }, [newTask.assignee]);

    useEffect(() => {
        // Fetch the task with taskId and update the state
        const fetchLkValues = async () => {
            if (currentUser !== null) {
                const authToken: string = await currentUser.getIdToken();
                const fetchedValues: TaskCreationLKValues | null =
                    await getTaskLKValues(authToken);
                setLkOptions(fetchedValues);
            }
        };

        if (lkOptions && currentUser) {
            let currentUserId = currentUser.uid;
            let formattedAssignees;
            // If SC; higher priviledge can have multiple users including SC
            if (currentRoles?.length === 1 && currentRoles[0].name === "SC") {
                const currentUserData = lkOptions.users?.find((user) => user.id === currentUserId);
                const displayName = currentUserData
                    ? `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim()
                    : "No Display Name";
                
                formattedAssignees = [
                    {
                        value: currentUser.uid,
                        label: displayName || "No Display Name",
                    },
                ];
                setNewTask({ ...newTask, ["assignee"]: currentUserId });
            } else {
                formattedAssignees = (lkOptions.users || []).map((user) => ({
                    value: user.id.toString(),
                    label: user.displayName,
                }));
            }

            const isSCHighestRole =
                currentRoles?.length === 1 && currentRoles[0].name === "SC"
                    ? true
                    : false;
            setIsSC(isSCHighestRole);

            if (isSCHighestRole && currentUser) {
                setAssigneeOptions([
                    {
                        value: currentUser?.uid,
                        label: currentUser?.displayName
                            ? currentUser.displayName
                            : "Assign to yourself",
                    },
                ]);
            } else {
                setAssigneeOptions(formattedAssignees);
            }
        }
        if (!lkOptions) {
            fetchLkValues();
        }
    }, [lkOptions]);

    const handleAssigneeChange = (selectedOption: SingleValue<OptionType>) => {
        const assignee = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, assignee });
    };

    const handleParticipantChange = (selectedOption: SingleValue<OptionType>) => {
        const participant = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, participant });
    };

    const handleCoAssigneeChange = (selectedOption: SingleValue<OptionType>) => {
        const coAssignee = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, coAssignee });
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setNewTask({ ...newTask, [name]: value });
    };

    const handleRequiredFileChanges = (selectedOptions: OptionType[]) => {
        const requiredFiles = selectedOptions
            ? selectedOptions.map((option) => option.value)
            : [];
        setNewTask({ ...newTask, requiredFiles });
    };

    const validateFields = () => {
        if (!newTask.title) {
            toast.error("Title is required.");
            return false;
        }
        if (!newTask.dueDate) {
            toast.error("Due date is required.");
            return false;
        }
        if (!newTask.description) {
            toast.error("Description is required.");
            return false;
        }
        if (!newTask.assignee) {
            toast.error("Assignee is required.");
            return false;
        }
        if (!newTask.participant) {
            toast.error("Participant is required.");
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validateFields()) {
            return;
        }
        // Handle submitting the new task
        if (newTask.templateTaskId !== undefined) {
            const createTask = async () => {
                if (currentUser !== null) {
                    const authToken: string = await currentUser.getIdToken();
                    const newTaskId: number | null = await publishTemplateInterimTask(
                        authToken,
                        newTask
                    );
                    return newTaskId;
                }
                return null;
            };
            const idPromise = createTask();

            idPromise.then((id) => {
                if (id !== null) {
                    // Redirect to new task here.
                    navigate(`/task/${currentOrganization?.abbreviation}-${id}`);
                } else {
                    toast.error("Failed to create task");
                }
            });
        } else {
            toast.error("Not ready");
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewTask({ ...newTask, [name]: value });
    };

    const getAllParticipantUnderMe = async () => {
        if (!currentUser || !newTask.assignee) return;
        const token = await currentUser.getIdToken();
        setParticipantOptions([]); // clear list
        setNewTask((prev) => ({ ...prev, participant: undefined, })); // clear from form
        setLoading(prev => ({ ...prev, participant: true, }));
        GetAllParticipantUnderMeAsync(token, newTask.assignee).then((res) => {
            if (res.success) {
                const formattedParticipants = res.data?.map((part) => ({
                    value: part.id.toString(),
                    label: part.firstName + " " + part.lastName,
                }));
                if (formattedParticipants) {
                    setParticipantOptions(formattedParticipants);
                }
            }
        }).finally(() => setLoading(prev => ({ ...prev, participant: false })))
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <div className="custom_input_flled">
                        <label htmlFor="title">Title</label>
                        <input
                            disabled={newTask.title === template?.title}
                            type="text"
                            id="title"
                            name="title"
                            placeholder="Give your task a title..."
                            value={newTask.title}
                            onChange={handleChange}
                            required
                            className={` ${newTask.title === template?.title
                                ? ""
                                : ""
                                }`}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="custom_input_flled">
                        <label
                            htmlFor="dueDate">Due Date</label>
                        <input
                            type="date"
                            id="dueDate"
                            disabled={
                                newTask.dueDate ===
                                getDateXDaysInFuture(template?.daysFromCreationDueDate)
                            }
                            name="dueDate"
                            value={newTask.dueDate}
                            onChange={handleDateChange}
                            className={`
              ${newTask.dueDate ===
                                    getDateXDaysInFuture(template?.daysFromCreationDueDate)
                                    ? ""
                                    : ""
                                }`}
                        />
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="custom_input_flled">
                        <label htmlFor="assignee" >Assignee</label>
                        <Select
                            id="assignee"
                            name="assignee"
                            value={assigneeOptions.find(
                                (option) => option.value === newTask.assignee
                            )}
                            onChange={handleAssigneeChange}
                            options={assigneeOptions}
                            className="react-select-container react-select-container_lg"
                            classNamePrefix="react-select"
                            isLoading={loading.participant}
                            placeholder={loading.participant ? "Loading..." : "Select..."}
                        />
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="custom_input_flled">
                        <label htmlFor="assignee">Participant</label>
                        <Select
                            id="participant"
                            name="participant"
                            value={
                                participantOptions.find(option => option.value === newTask.participant)
                                || null
                            }
                            onChange={handleParticipantChange}
                            options={participantOptions}
                            className="react-select-container react-select-container_lg"
                            classNamePrefix="react-select"
                            isLoading={loading.participant}
                            placeholder={loading.participant ? "Loading..." : "Select..."}
                        />
                    </div>
                </div>

            </div>
            {newTask.assignee && !isSC && (
                <div className="custom_input_flled">
                    <label htmlFor="coAssignee">Co-Assignee{" "} (Optional)</label>
                    <Select
                        id="coAssignee"
                        name="coAssignee"
                        value={assigneeOptions.find(
                            (option) => option.value === newTask.coAssignee
                        )}
                        onChange={handleCoAssigneeChange}
                        options={assigneeOptions?.filter(
                            (user) => user.value !== newTask.assignee
                        )}
                        className="react-select-container react-select-container_lg"
                        classNamePrefix="react-select"
                    />
                </div>
            )}
            <div className="custom_input_flled">
                <label htmlFor="description">Description</label>
                <textarea
                    disabled={newTask.description === template?.description}
                    id="description"
                    name="description"
                    placeholder="A brief description of what the task entails..."
                    value={newTask.description}
                    onChange={handleChange}
                    required
                    className={` ${newTask.description === template?.description
                        ? ""
                        : ""
                        }`}
                />
            </div>
            <div className="stats">
                <p>*{newTask.requiredFiles.length} required files will be added withthis task*</p>
            </div>
            <div className="form_btn_flx mby_t_24">
                <Button variant="primary" onClick={handleSubmit}>Create Task</Button>
            </div>
        </>
    );
};

export default TaskCreationFromTemplate;
